import React, { useEffect, useState } from "react";
import { api, money, fmtDate } from "@/lib/api";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell,
} from "recharts";
import {
  CurrencyDollar as CurrencyDollarIcon, Users as UsersIcon, Truck as TruckIcon,
  Warning as WarningIcon, Package as PackageIcon, TrendUp as TrendUpIcon,
  Sparkle as SparkleIcon, ShieldWarning as ShieldWarningIcon,
} from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value, tone = "default", sub }) {
  const tones = {
    default: "text-primary bg-primary/10",
    red: "text-destructive bg-destructive/10",
    green: "text-emerald-600 bg-emerald-500/10",
    amber: "text-amber-600 bg-amber-500/10",
  };
  return (
    <div className="border rounded-lg bg-card p-4 hover:-translate-y-0.5 transition-transform">
      <div className="flex items-center justify-between">
        <span className={`h-9 w-9 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon size={20} weight="duotone" /></span>
      </div>
      <div className="mt-3 font-head text-2xl font-bold tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [insights, setInsights] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data)).catch(() => {});
    api.get("/ai/insights").then((r) => setInsights(r.data.insights)).catch(() => setInsights([]));
  }, []);

  useEffect(() => {
    api.get(`/dashboard/monthly?year=${year}`).then((r) => setMonthly(r.data)).catch(() => {});
  }, [year]);

  if (!s) return <div className="text-muted-foreground py-10 text-center">Loading dashboard...</div>;

  const barColors = ["#0057B8", "#D62828", "#0ea5e9", "#f59e0b", "#10b981"];

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-head text-2xl font-bold">Operations Overview</h1>
        <p className="text-muted-foreground text-sm">Real-time performance for Lucas &amp; Sons — {fmtDate(new Date().toISOString())}</p>
      </div>

      {/* highlight metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-lg p-4 border bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-900">
          <div className="flex items-center gap-2 text-primary"><CurrencyDollarIcon size={18} weight="duotone" /><span className="text-xs font-medium">Daily Sales</span></div>
          <div className="mt-2 font-head text-2xl lg:text-3xl font-bold">{money(s.daily_sales)}</div>
        </div>
        <div className="rounded-lg p-4 border bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-900">
          <div className="flex items-center gap-2 text-primary"><TrendUpIcon size={18} weight="duotone" /><span className="text-xs font-medium">Monthly Sales</span></div>
          <div className="mt-2 font-head text-2xl lg:text-3xl font-bold">{money(s.monthly_sales)}</div>
        </div>
        <div className="rounded-lg p-4 border bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-900">
          <div className="flex items-center gap-2 text-primary"><CurrencyDollarIcon size={18} weight="duotone" /><span className="text-xs font-medium">Annual Revenue</span></div>
          <div className="mt-2 font-head text-2xl lg:text-3xl font-bold">{money(s.annual_revenue)}</div>
        </div>
        <div className="rounded-lg p-4 border bg-gradient-to-br from-red-50 to-white dark:from-red-950/30 dark:to-slate-900">
          <div className="flex items-center gap-2 text-destructive"><WarningIcon size={18} weight="duotone" /><span className="text-xs font-medium">Outstanding Balances</span></div>
          <div className="mt-2 font-head text-2xl lg:text-3xl font-bold text-destructive">{money(s.outstanding_balances)}</div>
        </div>
      </div>

      {/* stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat icon={UsersIcon} label="Total Customers" value={s.total_customers} sub={`${s.active_customers} active`} />
        <Stat icon={CurrencyDollarIcon} label="Weekly Sales" value={money(s.weekly_sales)} tone="green" />
        <Stat icon={TruckIcon} label="Fleet Active" value={`${s.fleet_active}/${s.fleet_total}`} />
        <Stat icon={PackageIcon} label="Bulk LPG (gal)" value={Number(s.bulk_tank_inventory).toLocaleString()} />
        <Stat icon={WarningIcon} label="Low Stock Alerts" value={s.low_stock_alerts} tone="red" />
        <Stat icon={ShieldWarningIcon} label="Safety Alerts" value={s.safety_alerts} tone="red" />
      </div>

      {/* charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border rounded-lg bg-card p-4">
          <h3 className="font-head font-semibold text-sm mb-3">Sales Trend — Last 7 Days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={s.sales_trend}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0057B8" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#0057B8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="sales" stroke="#0057B8" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="border rounded-lg bg-card p-4">
          <h3 className="font-head font-semibold text-sm mb-3">Top Selling Products</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={s.top_sizes} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={120} stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="qty" radius={[0, 4, 4, 0]}>
                {s.top_sizes.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* monthly sales chart with year selector */}
      <div className="border rounded-lg bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-semibold text-sm flex items-center gap-2"><TrendUpIcon size={18} weight="duotone" className="text-primary" /> Monthly Sales Chart</h3>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28 h-8" data-testid="dashboard-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(monthly?.years?.length ? monthly.years : [year]).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthly?.months || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => money(v)} />
            <Bar dataKey="sales" name="Sales" fill="#0057B8" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="text-xs text-muted-foreground mt-1">Year total: <span className="font-semibold text-foreground">{money(monthly?.total || 0)}</span></div>
      </div>

      {/* AI insights + recent transactions */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="border rounded-lg bg-card p-4">
          <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2"><SparkleIcon size={18} weight="duotone" className="text-primary" /> AI Smart Insights</h3>
          {!insights && <div className="text-sm text-muted-foreground">Analyzing operations...</div>}
          <div className="space-y-3">
            {insights?.map((it, i) => (
              <div key={i} className="border-l-2 border-primary pl-3">
                <div className="text-sm font-medium">{it.title}</div>
                <div className="text-xs text-muted-foreground">{it.detail}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 border rounded-lg bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head font-semibold text-sm">Recent Transactions</h3>
            <Link to="/pos" className="text-xs text-primary hover:underline">Go to POS →</Link>
          </div>
          <div className="divide-y">
            {s.recent_transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2">
                <div>
                  <div className="text-sm font-medium">{t.customer_name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{t.invoice_no} · {t.payment_type}</div>
                </div>
                <div className="text-sm font-semibold">{money(t.total)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
